import * as tslib_1 from "tslib";
import { HttpClient } from '@angular/common/http';
import Cookies from 'js-cookie';
import { reduce } from 'lodash';
import { Observable, throwError, timer, of } from 'rxjs';
import { tap, distinctUntilChanged, switchMap, catchError, take } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { env } from '@envs/env';
import { config } from '@config/config';
import { UrlUtils } from '@app/utils/url.utils';
import { JwtService } from '@app/utils/jwt.service';
import { AuthState, AuthStatus, ChangeStatus, SetAuthData } from '@app/auth/auth.state';
import { trackEvent } from '@app/utils/amplitude.utils';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../utils/jwt.service";
import * as i3 from "@ngxs/store";
var OAuthService = /** @class */ (function () {
    function OAuthService(http, jwt, store) {
        this.http = http;
        this.jwt = jwt;
        this.store = store;
        this.timer$ = timer(0, env.auth.interval);
        this.trackEvent = trackEvent;
        /** Same proactive refresh window as supportal (seconds before access token expiry). */
        this.accessTokenRefreshBufferSeconds = 5 * 60;
        this.init();
    }
    // set up and watch authevents to trigger login acitons
    OAuthService.prototype.init = function () {
        this.authEvents$ = this.getAuthEvents();
        this.startAuthInterval();
        var token = this.getAccessToken();
        if (token) {
            this.setAccessToken(token);
        }
        // start watching
        this.subscribeToAuthEvents();
    };
    OAuthService.prototype.getAuthEvents = function () {
        return this.store.select(AuthState.status).pipe(distinctUntilChanged());
    };
    OAuthService.prototype.startAuthInterval = function () {
        var _this = this;
        this.timer$.pipe(switchMap(function () { return _this.updateAuthState(); })).subscribe();
    };
    // watch auth events and update auth status
    OAuthService.prototype.subscribeToAuthEvents = function () {
        var _this = this;
        this.authEvents$.subscribe(function (status) {
            if (status === AuthStatus.AUTH_VALID) {
                var token_1 = _this.getAccessToken();
                _this.http.get(_this.jwt.getBaseUrl(token_1)).subscribe(function (res) {
                    _this.store.dispatch(new SetAuthData(_this.jwt.getBaseUrl(token_1), _this.jwt.getScopes(token_1), res.data[0]));
                    _this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_COMPLETE));
                });
            }
        });
    };
    OAuthService.prototype.updateAuthState = function () {
        var _this = this;
        var at = this.getAccessToken();
        var rt = this.getRefreshToken();
        var tokens = {
            at: at === '' ? false : true,
            rt: rt === '' ? false : true,
        };
        return this.authEvents$.pipe(take(1), tap(function (status) {
            if (tokens.at === false && tokens.rt === false) {
                _this.completeAuthorizationCodeFlow().subscribe({
                    error: function (err) {
                        var error;
                        if (typeof err === 'string') {
                            error = err;
                        }
                        else if (err && err.error && err.error.detailed[0]) {
                            if (err.error.message === 'unauthorized_client') {
                                error = 'Access Denied.';
                            }
                            else {
                                error = err.error.detailed[0];
                            }
                        }
                        _this.initAuthorizationCodeFlow(error);
                    },
                });
            }
            else if (tokens.at === false && tokens.rt === true) {
                _this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_NO_ACCESS));
                _this.refreshAccessToken().subscribe();
            }
            else if (tokens.at === true && tokens.rt === false && status !== AuthStatus.AUTH_COMPLETE) {
                _this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_NO_REFRESH));
                _this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
            }
            else if (_this.jwt.isTokenExpired(_this.jwt.decodeToken(_this.getAccessToken()), _this.accessTokenRefreshBufferSeconds)) {
                _this.refreshAccessToken().subscribe();
            }
            else if (status !== AuthStatus.AUTH_COMPLETE) {
                _this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
            }
            else {
                _this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_COMPLETE));
            }
        }));
    };
    OAuthService.prototype.isLoggedIn = function () {
        return !!this.getAccessToken();
    };
    OAuthService.prototype.initAuthorizationCodeFlow = function (error) {
        this.trackEvent('Auth Init Login');
        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_INVALID));
        var state = this.generateState();
        this.saveState(state);
        var loginUrl = this.getStaticLoginUrl(state, error);
        if (!env.testing) {
            window.location.href = loginUrl;
        }
    };
    // Navigate to static login page
    OAuthService.prototype.getStaticLoginUrl = function (state, error) {
        var url = this.generateRedirectUrl(state, window.location.origin);
        if (error) {
            url += "&error=" + error;
        }
        return url;
    };
    OAuthService.prototype.generateRedirectUrl = function (state, uri) {
        var url = env.apiUrl + "/account/login?client_id=" + env.auth.clientId +
            ("&redirect_uri=" + encodeURIComponent(uri) + "&state=" + state + "&response_type=code");
        return url;
    };
    OAuthService.prototype.completeAuthorizationCodeFlow = function () {
        var _this = this;
        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_IN_PROGRESS));
        var params = UrlUtils.getUrlParams();
        if (!params.code || !params.state) {
            return throwError(null);
        }
        // Compare before clearing. Cookie/session was cleared on a prior attempt if the auth
        // interval fired again while the token exchange was still in flight (timer every 5s),
        // which produced a false "Invalid State".
        var storedState = this.peekState();
        if (params.state !== storedState) {
            trackEvent('Auth Invalid State');
            return throwError('Invalid State');
        }
        // Consume the callback URL immediately so repeat interval ticks do not re-enter with
        // code/state still in the address bar after stored state was cleared.
        this.stripOAuthParamsFromUrl();
        this.getAndClearState();
        return this.http
            .post(env.apiUrl + "/oauth/token", {
            client_id: env.auth.clientId,
            code: params.code,
            grant_type: 'authorization_code',
            redirect_uri: window.location.origin,
        })
            .pipe(tap(function (res) {
            trackEvent('Auth Login Successful');
            var tokens = res.data[0];
            _this.saveAccessToken(tokens.access_token, tokens.expires_in);
            _this.saveRefreshToken(tokens.refresh_token);
            _this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
        }, function () {
            trackEvent('Auth Login Unsuccessful');
            _this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_INVALID));
        }));
    };
    OAuthService.prototype.refreshAccessToken = function () {
        var _this = this;
        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_REFRESHING));
        var authParams = "grant_type=refresh_token&client_id=" + env.auth.clientId + "&refresh_token=" + this.getRefreshToken();
        return this.http
            .post(env.apiUrl + "/oauth/token", encodeURI(authParams), {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }) // eslint-disable-line @typescript-eslint/indent
            .pipe(tap(function (res) {
            trackEvent('Auth Token Refreshed');
            var tokens = res.data[0];
            _this.saveAccessToken(tokens.access_token, tokens.expires_in);
            _this.saveRefreshToken(tokens.refresh_token);
            _this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
        }, function () {
            _this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_INVALID));
        }));
    };
    /**
     * Revokes the session on the server (Bearer access token + JSON body with refresh_token).
     * Clears local tokens and starts login only after the request finishes.
     */
    OAuthService.prototype.logout = function () {
        var _this = this;
        var rt = this.getRefreshToken();
        var request$ = rt
            ? this.http.post(env.apiUrl + "/oauth/logout", { refresh_token: rt }, {
                headers: { 'Content-Type': 'application/json' },
            })
            : of(null);
        return request$.pipe(catchError(function () { return of(null); }), tap(function () {
            _this.clearTokens();
            _this.initAuthorizationCodeFlow();
        }));
    };
    /**
     * Generates a random value for OAuth state
     * @returns Series of hexadecimal characters
     */
    OAuthService.prototype.getAccessToken = function () {
        return Cookies.get(env.auth.accessTokenName) || '';
    };
    OAuthService.prototype.getRefreshToken = function () {
        return Cookies.get(env.auth.refreshTokenName) || '';
    };
    OAuthService.prototype.clearTokens = function () {
        var opts = this.getAuthCookieOpts();
        Cookies.remove(env.auth.accessTokenName, opts);
        Cookies.remove(env.auth.refreshTokenName, opts);
    };
    OAuthService.prototype.setAccessToken = function (tok) {
        this.store.dispatch(new SetAuthData(this.jwt.getBaseUrl(tok), this.jwt.getScopes(tok), { id: this.jwt.getTokenId(tok) }));
    };
    OAuthService.prototype.generateState = function () {
        // Length is halved because there are 2 hex chars in 1 uint8
        var arr = new Uint8Array(config.authStateLength / 2);
        // Generate random values (msCrypto for IE 11)
        if (window.msCrypto) {
            window.msCrypto.getRandomValues(arr);
        }
        else {
            window.crypto.getRandomValues(arr);
        }
        // Converts random values to hexadecimal array, then joins into a string
        return reduce(arr, function (acc, val) { return acc + ('0' + val.toString(16)).slice(-2); }, '');
    };
    OAuthService.prototype.getBaseHost = function () {
        var parts = window.location.hostname.split('.');
        return [parts[parts.length - 2], parts[parts.length - 1]].join('.');
    };
    /** Shared attributes so set/remove stay in sync (incl. SameSite for CSRF-style hardening). */
    OAuthService.prototype.getAuthCookieOpts = function () {
        return {
            secure: env.auth.https,
            domain: env.auth.https ? "." + this.getBaseHost() : 'localhost',
            sameSite: 'lax',
        };
    };
    OAuthService.prototype.saveAccessToken = function (token, expiresIn) {
        Cookies.set(env.auth.accessTokenName, token, tslib_1.__assign({}, this.getAuthCookieOpts(), { expires: new Date(Date.now() + expiresIn * 1000) }));
    };
    OAuthService.prototype.saveRefreshToken = function (token) {
        Cookies.set(env.auth.refreshTokenName, token, tslib_1.__assign({}, this.getAuthCookieOpts(), { expires: env.auth.refreshTokenExpires }));
    };
    OAuthService.prototype.saveState = function (state) {
        // Per-tab storage avoids cross-tab races where a second login overwrites the shared
        // oauth_state cookie and the first tab returns with a mismatched state.
        sessionStorage.setItem(env.auth.stateTokenName, state);
    };
    OAuthService.prototype.peekState = function () {
        return sessionStorage.getItem(env.auth.stateTokenName) || Cookies.get(env.auth.stateTokenName) || '';
    };
    OAuthService.prototype.getAndClearState = function () {
        var key = env.auth.stateTokenName;
        var state = sessionStorage.getItem(key) || Cookies.get(key) || '';
        sessionStorage.removeItem(key);
        Cookies.remove(key, this.getAuthCookieOpts());
        return state;
    };
    /** Removes OAuth callback params so UrlUtils and repeat auth polling do not see a stale code/state pair. */
    OAuthService.prototype.stripOAuthParamsFromUrl = function () {
        try {
            var url = new URL(window.location.href);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            var path = url.pathname + url.search + url.hash;
            window.history.replaceState(window.history.state, document.title, path);
        }
        catch (_a) {
            // ignore replaceState errors in non-browser or exotic URLs
        }
    };
    OAuthService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function OAuthService_Factory() { return new OAuthService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.JwtService), i0.ɵɵinject(i3.Store)); }, token: OAuthService, providedIn: "root" });
    return OAuthService;
}());
export { OAuthService };
